import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    // Delay sync so Shopify finishes cart update first
    setTimeout(() => {
      this.#syncBasketFee();
    }, 300);
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    // Sync when drawer opens
    setTimeout(() => {
      this.#syncBasketFee();
    }, 300);
  }

  close() {
    this.closeDialog();
  }

  async #syncBasketFee() {
    if (this._syncingFee) return;
    this._syncingFee = true;

    const FEE_HANDLE = 'basket-shipping-fee';
    const FEE_VARIANT_ID = window.BASKET_SHIPPING_FEE_VARIANT_ID;
    const BASKET_HANDLES = [
      'super-lux-gift-basket',
      'lux-gift-basket'
    ];

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      let basketQty = 0;
      let feeItem = null;

      cart.items.forEach(item => {
        if (item.handle === FEE_HANDLE) {
          feeItem = item;
        }
        if (BASKET_HANDLES.includes(item.handle)) {
          basketQty += item.quantity;
        }
      });

      // CASE 1: Baskets exist → ensure correct fee quantity
      if (basketQty > 0) {
        if (!feeItem || feeItem.quantity !== basketQty) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: FEE_VARIANT_ID,
              quantity: basketQty
            })
          });
        }
      }

      // CASE 2: No baskets → remove fee cleanly
      if (basketQty === 0 && feeItem) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: feeItem.key,
            quantity: 0
          })
        });
      }

    } catch (e) {
      console.error('Basket shipping fee error:', e);
    }

    // Allow future updates
    setTimeout(() => {
      this._syncingFee = false;
    }, 300);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

